import React, { useState, useRef } from "react";

const MAX_BYTES = 512 * 1024; // 512 KB (mirrors server)

// Custom Azure pipeline (azure-pipelines.yml) upload — Mule only.
// data: { uploadEndpoint, options[], canSkip, skipHint }
export default function PipelineUploadCard({ data, sessionId, backendUrl, onSubmit }) {
  const [mode, setMode]         = useState("default"); // 'default' | 'upload'
  const [file, setFile]         = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded]   = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [warnings, setWarnings]   = useState([]);
  const inputRef = useRef();

  const pickFile = (f) => {
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["yml", "yaml"].includes(ext)) {
      setUploadErr("Only .yml or .yaml files are accepted.");
      return;
    }
    if (f.size > MAX_BYTES) {
      setUploadErr("Pipeline YAML exceeds the 512 KB limit.");
      return;
    }
    if (f.size === 0) {
      setUploadErr("The selected file is empty.");
      return;
    }
    setFile(f);
    setUploaded(false);
    setWarnings([]);
    setUploadErr("");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) pickFile(dropped);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setUploadErr("");
    setWarnings([]);
    try {
      const form = new FormData();
      form.append("file", file);          // field name MUST be "file"
      form.append("sessionId", sessionId);
      const res = await fetch(backendUrl + data.uploadEndpoint, {
        method: "POST",
        headers: { "x-access-token": localStorage.getItem("token") || "" }, // let the browser set the multipart boundary
        body: form,
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || json.message || "Upload failed");
      setUploaded(true);
      setWarnings(Array.isArray(json.warnings) ? json.warnings : []);
    } catch (err) {
      setUploadErr(err.message || "Upload failed. Please try again.");
      setUploaded(false);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="sh-existing-api-card">
      {/* Option chooser */}
      <div className="sh-existing-api-tabs">
        <button
          className={`sh-existing-api-tab${mode === "default" ? " active" : ""}`}
          onClick={() => setMode("default")}
        >
          ⚙️ Built-in pipeline
        </button>
        <button
          className={`sh-existing-api-tab${mode === "upload" ? " active" : ""}`}
          onClick={() => setMode("upload")}
        >
          📤 Upload my own
        </button>
      </div>

      {mode === "default" && (
        <div>
          <div style={{ fontSize: 12, color: "#7a93bc", padding: "6px 2px 10px", lineHeight: 1.6 }}>
            Use the built-in Azure DevOps pipeline template (recommended). Nothing to upload.
          </div>
          <div className="sh-existing-api-actions">
            <button className="sh-existing-api-btn-primary" onClick={() => onSubmit({ type: "skip" })}>
              Continue →
            </button>
          </div>
        </div>
      )}

      {mode === "upload" && (
        <div>
          <div
            className={`sh-existing-api-dropzone${dragOver ? " drag-over" : ""}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            {file
              ? <span>📄 {file.name} <span style={{ opacity: 0.6, fontSize: 11 }}>({(file.size / 1024).toFixed(1)} KB)</span></span>
              : <span>Drop <strong>azure-pipelines.yml</strong> here, or click to browse <span style={{ opacity: 0.6, fontSize: 11 }}>(.yml / .yaml, ≤ 512 KB)</span></span>
            }
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".yml,.yaml"
            style={{ display: "none" }}
            onChange={(e) => { if (e.target.files[0]) pickFile(e.target.files[0]); }}
          />

          {uploadErr && <div className="sh-existing-api-error">{uploadErr}</div>}
          {uploaded && (
            <div style={{ fontSize: 12, color: "#2EBFA5", marginTop: 8 }}>
              ✓ Uploaded {file?.name}
              {warnings.map((w, i) => (
                <div key={i} style={{ color: "#f0b429", marginTop: 4 }}>⚠ {w}</div>
              ))}
            </div>
          )}

          <div className="sh-existing-api-actions">
            {!uploaded ? (
              <button
                className="sh-existing-api-btn-primary"
                disabled={!file || uploading}
                onClick={handleUpload}
              >
                {uploading ? "Uploading…" : "Upload"}
              </button>
            ) : (
              <button
                className="sh-existing-api-btn-primary"
                onClick={() => onSubmit({ type: "uploaded" })}
              >
                Continue →
              </button>
            )}
          </div>
        </div>
      )}

      {data.canSkip && mode === "upload" && (
        <div style={{ marginTop: 10, textAlign: "center" }}>
          <button className="sh-existing-api-skip" onClick={() => onSubmit({ type: "skip" })}>
            Skip — {data.skipHint}
          </button>
        </div>
      )}
    </div>
  );
}
